import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import {
  FileText, Filter, Download, Search, RotateCcw, Calendar,
  User, ShieldAlert, CheckCircle2, XCircle, Clock, Eye,
  ArrowRight, ChevronLeft, ChevronRight, Activity, Building,
  Layers, Lock, Sparkles, RefreshCw, UserCheck, Tag, Cpu
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatApiError } from '../lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RESOURCE_OPTIONS = [
  { value: 'all', label: 'All Modules' },
  { value: 'auth', label: 'Authentication & Access' },
  { value: 'employee', label: 'Employee & User Mgmt' },
  { value: 'user', label: 'User Accounts' },
  { value: 'company', label: 'Company & Branch Scope' },
  { value: 'department', label: 'Department Master' },
  { value: 'designation', label: 'Designation Master' },
  { value: 'unified_policy', label: 'Unified Policy Master' },
  { value: 'local_conveyance_policy', label: 'Local Conveyance Policy' },
  { value: 'outstation_conveyance_policy', label: 'Outstation Travel Policy' },
  { value: 'booking_controller', label: 'Booking Controller' },
  { value: 'payroll', label: 'Payroll & HR Operations' },
  { value: 'leave', label: 'Leave & Attendance' },
  { value: 'local_conveyance', label: 'Local Conveyance Claims' },
  { value: 'outstation_conveyance', label: 'Outstation Travel Claims' },
  { value: 'system', label: 'System & Configuration' },
];

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'tenant_admin', label: 'Tenant Admin' },
  { value: 'COMPANY_ADMIN', label: 'Company Admin' },
  { value: 'hr_admin', label: 'HR Admin' },
  { value: 'payroll_admin', label: 'Payroll Admin' },
  { value: 'department_manager', label: 'Department Manager' },
  { value: 'employee', label: 'Employee' },
];

const RESULT_OPTIONS = [
  { value: 'all', label: 'All Results' },
  { value: 'SUCCESS', label: 'Success Only' },
  { value: 'FAILED', label: 'Failed / Denied Only' },
];

const AuditLogs = () => {
  // Data states
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState({
    total_events: 0,
    today_events: 0,
    success_count: 0,
    failed_count: 0,
  });

  // Filter states
  const [search, setSearch] = useState('');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Selected Log Modal state
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...(search.trim() && { search: search.trim() }),
        ...(resourceFilter !== 'all' && { resource: resourceFilter }),
        ...(roleFilter !== 'all' && { actor_role: roleFilter }),
        ...(resultFilter !== 'all' && { result: resultFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const { data } = await axios.get(`${API_URL}/audit-logs`, {
        params,
        withCredentials: true,
      });

      if (data && Array.isArray(data.items)) {
        setLogs(data.items);
        setTotalCount(data.total || 0);
        setTotalPages(data.pages || 1);
        if (data.summary) setSummary(data.summary);
      } else if (Array.isArray(data)) {
        setLogs(data);
        setTotalCount(data.length);
        setTotalPages(1);
      } else {
        setLogs([]);
        setTotalCount(0);
        setTotalPages(1);
      }
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to fetch audit logs'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, resourceFilter, roleFilter, resultFilter, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleResetFilters = () => {
    setSearch('');
    setResourceFilter('all');
    setRoleFilter('all');
    setResultFilter('all');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = {
        ...(search.trim() && { search: search.trim() }),
        ...(resourceFilter !== 'all' && { resource: resourceFilter }),
        ...(roleFilter !== 'all' && { actor_role: roleFilter }),
        ...(resultFilter !== 'all' && { result: resultFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await axios.get(`${API_URL}/audit-logs/export/csv`, {
        params,
        responseType: 'blob',
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Audit log CSV exported successfully');
    } catch (error) {
      toast.error(formatApiError(error, 'CSV Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = async (logId) => {
    try {
      const { data } = await axios.get(`${API_URL}/audit-logs/${logId}`, {
        withCredentials: true,
      });
      setSelectedLog(data);
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to load audit log details'));
    }
  };

  const getActionBadgeStyle = (action = '', result = 'SUCCESS') => {
    if (result === 'FAILED') return 'bg-rose-50 text-rose-700 border-rose-200';
    const lower = action.toLowerCase();
    if (lower.includes('created') || lower.includes('applied') || lower.includes('submitted'))
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (lower.includes('updated') || lower.includes('modified') || lower.includes('changed'))
      return 'bg-sky-50 text-sky-700 border-sky-200';
    if (lower.includes('deleted') || lower.includes('removed') || lower.includes('rejected'))
      return 'bg-amber-50 text-amber-700 border-amber-200';
    if (lower.includes('approved') || lower.includes('released') || lower.includes('signed'))
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    if (lower.includes('login') || lower.includes('logout'))
      return 'bg-purple-50 text-purple-700 border-purple-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getRoleBadgeStyle = (role = '') => {
    const r = role.toLowerCase();
    if (r.includes('tenant') || r.includes('company_admin')) return 'bg-purple-100 text-purple-800';
    if (r.includes('hr')) return 'bg-blue-100 text-blue-800';
    if (r.includes('payroll')) return 'bg-emerald-100 text-emerald-800';
    if (r.includes('manager')) return 'bg-amber-100 text-amber-800';
    return 'bg-slate-100 text-slate-700';
  };

  const formatTimestamp = (ts) => {
    if (!ts) return 'N/A';
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return String(ts);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header Banner */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950 p-6 rounded-2xl text-white shadow-xl">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-500/20 rounded-xl border border-indigo-400/30 backdrop-blur-sm">
                <ShieldAlert className="w-7 h-7 text-indigo-300" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                  Tenant Audit Trail & Activity Logs
                </h1>
                <p className="text-slate-300 text-sm mt-0.5">
                  Complete, accurate audit logging with explicit actor identification and target tracking.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex items-center gap-3">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-2 px-3.5 py-2 bg-slate-800/80 hover:bg-slate-700 border border-slate-700 rounded-xl text-sm font-medium transition-colors text-slate-200"
              title="Refresh Audit Trail"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              data-testid="audit-export-btn"
              onClick={handleExportCSV}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-semibold shadow-lg shadow-indigo-600/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting CSV...' : 'Export Audit CSV'}
            </button>
          </div>
        </div>

        {/* 4 Summary Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Recorded Events</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{summary.total_events.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <Activity className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Today's Activity</p>
              <p className="text-2xl font-bold text-sky-600 mt-1">{summary.today_events.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-sky-50 text-sky-600 rounded-xl">
              <Clock className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Successful Operations</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{summary.success_count.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <CheckCircle2 className="w-6 h-6" />
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Failed / Denied Attempts</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">{summary.failed_count.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-rose-50 text-rose-600 rounded-xl">
              <XCircle className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Search & Multi-Filter Control Toolbar */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Keyword Search Input */}
            <div className="flex-1 min-w-[260px] relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search Actor Name, Employee ID (EMP...), Target, Action..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-colors"
                data-testid="audit-log-search-input"
              />
            </div>

            {/* Resource Module Filter */}
            <Select value={resourceFilter} onValueChange={(val) => { setResourceFilter(val); setPage(1); }}>
              <SelectTrigger className="w-48 bg-slate-50 border-slate-200 rounded-xl" data-testid="audit-log-filter">
                <SelectValue placeholder="Resource Module" />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={(val) => { setRoleFilter(val); setPage(1); }}>
              <SelectTrigger className="w-44 bg-slate-50 border-slate-200 rounded-xl">
                <SelectValue placeholder="Actor Role" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Result Filter */}
            <Select value={resultFilter} onValueChange={(val) => { setResultFilter(val); setPage(1); }}>
              <SelectTrigger className="w-40 bg-slate-50 border-slate-200 rounded-xl">
                <SelectValue placeholder="Result Status" />
              </SelectTrigger>
              <SelectContent>
                {RESULT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Reset Filters Button */}
            <button
              onClick={handleResetFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl text-sm font-medium transition-colors"
              title="Reset all filters"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>

          {/* Date Range Picker Row */}
          <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-slate-100 text-xs font-medium text-slate-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <span>Date Filter:</span>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-slate-500">From:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-slate-500">To:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                className="px-2.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs"
              />
            </div>
          </div>
        </div>

        {/* Enhanced Audit Logs Table */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-500 text-[11px] font-bold uppercase tracking-wider">
                  <th className="py-3.5 px-4">Timestamp</th>
                  <th className="py-3.5 px-4">Performed By (Actor)</th>
                  <th className="py-3.5 px-4">Role</th>
                  <th className="py-3.5 px-4">Action Event</th>
                  <th className="py-3.5 px-4">Resource</th>
                  <th className="py-3.5 px-4">Affected Target / Employee</th>
                  <th className="py-3.5 px-4">Result</th>
                  <th className="py-3.5 px-4">Narrative Preview</th>
                  <th className="py-3.5 px-4 text-center">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-16 text-slate-500">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
                        <span className="font-medium text-slate-600">Loading audit records...</span>
                      </div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-16 text-slate-500">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <FileText className="w-10 h-10 text-slate-300" />
                        <p className="font-semibold text-slate-700">No audit log records found</p>
                        <p className="text-xs text-slate-500">Try adjusting your filters or keyword search.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const actionLabel = log.action_label || log.action || 'Unknown';
                    const actorName = log.actor_name || log.user_id || 'System Service Account';
                    const actorCode = log.actor_code || '-';
                    const role = log.actor_role || 'User';
                    const resName = (log.resource || '-').replace('_', ' ').toUpperCase();
                    const targetName = log.target_name || log.resource_id || '-';
                    const targetEmpName = log.target_employee_name !== '-' ? log.target_employee_name : null;
                    const targetEmpCode = log.target_employee_code !== '-' ? log.target_employee_code : null;
                    const resultStatus = log.result || 'SUCCESS';
                    const narrative = log.description || log.remarks || '-';

                    return (
                      <tr
                        key={log.id || log._id}
                        data-testid={`audit-log-row-${log.id || log._id}`}
                        className="hover:bg-slate-50/80 transition-colors group cursor-pointer"
                        onClick={() => handleViewDetails(log.id || log._id)}
                      >
                        {/* Timestamp */}
                        <td className="py-3 px-4 text-xs text-slate-600 font-mono whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </td>

                        {/* Actor (Performed By) Column */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="font-semibold text-slate-900 group-hover:text-indigo-600 transition-colors">
                            {actorName}
                          </div>
                          {actorCode !== '-' ? (
                            <span className="inline-block px-1.5 py-0.5 bg-slate-100 text-slate-700 border border-slate-200 rounded text-[11px] font-mono mt-0.5">
                              ID: {actorCode}
                            </span>
                          ) : (
                            <span className="text-[11px] text-slate-400 font-mono">System Account</span>
                          )}
                        </td>

                        {/* Actor Role */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-medium ${getRoleBadgeStyle(role)}`}>
                            {role}
                          </span>
                        </td>

                        {/* Action Event Badge */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${getActionBadgeStyle(log.action, resultStatus)}`}>
                            {actionLabel}
                          </span>
                        </td>

                        {/* Resource Module Tag */}
                        <td className="py-3 px-4 text-xs font-medium text-slate-600 whitespace-nowrap">
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-[11px] border border-slate-200">
                            {resName}
                          </span>
                        </td>

                        {/* Affected Target / Employee */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          {targetEmpName ? (
                            <div>
                              <div className="font-medium text-slate-900 text-xs">{targetEmpName}</div>
                              {targetEmpCode && (
                                <span className="text-[10px] font-mono text-indigo-600 font-semibold">ID: {targetEmpCode}</span>
                              )}
                            </div>
                          ) : (
                            <div className="font-mono text-xs text-slate-700 max-w-[140px] truncate" title={targetName}>
                              {targetName}
                            </div>
                          )}
                        </td>

                        {/* Result Status */}
                        <td className="py-3 px-4 whitespace-nowrap">
                          {resultStatus === 'SUCCESS' ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-rose-700 text-xs font-semibold">
                              <XCircle className="w-3.5 h-3.5" /> Failed
                            </span>
                          )}
                        </td>

                        {/* Narrative Preview */}
                        <td className="py-3 px-4 text-xs text-slate-600 max-w-[240px] truncate" title={narrative}>
                          {narrative}
                        </td>

                        {/* Inspect Eye Button */}
                        <td className="py-3 px-4 text-center whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(log.id || log._id);
                            }}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Inspect Details & Diffs"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-slate-200 bg-slate-50/50 text-xs text-slate-600">
            <div>
              Showing <span className="font-semibold text-slate-900">{logs.length > 0 ? (page - 1) * limit + 1 : 0}</span> to{' '}
              <span className="font-semibold text-slate-900">{Math.min(page * limit, totalCount)}</span> of{' '}
              <span className="font-semibold text-slate-900">{totalCount}</span> entries
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span>Per Page:</span>
                <Select value={String(limit)} onValueChange={(val) => { setLimit(Number(val)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8 bg-white border-slate-200 text-xs rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="font-semibold px-2">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || loading}
                  className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Log Inspection Modal */}
        {selectedLog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-slate-200">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-slate-900 text-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500/20 rounded-xl border border-indigo-400/30">
                    <Sparkles className="w-5 h-5 text-indigo-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">{selectedLog.action_label || 'Audit Event Details'}</h3>
                    <p className="text-xs text-slate-300 font-mono">Log ID: {selectedLog.id || selectedLog._id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                >
                  ✕
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto space-y-6">
                {/* Narrative Summary Box */}
                <div className="p-4 bg-indigo-50/60 border border-indigo-100 rounded-xl space-y-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-indigo-800">Activity Summary & Narrative</p>
                  <p className="text-sm text-slate-800 font-medium leading-relaxed">
                    {selectedLog.description}
                  </p>
                </div>

                {/* Performed By (Actor) vs Affected Target Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Actor Card */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5">
                      <UserCheck className="w-4 h-4 text-indigo-600" /> Performed By (Actor)
                    </h4>
                    <div className="text-xs space-y-1.5">
                      <div className="flex justify-between"><span className="text-slate-500">Employee Name:</span> <span className="font-bold text-slate-900">{selectedLog.actor_name}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Employee ID:</span> <span className="font-mono font-semibold text-indigo-700">{selectedLog.actor_code}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">User Role:</span> <span className="font-medium text-slate-800">{selectedLog.actor_role}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Department:</span> <span className="text-slate-800">{selectedLog.actor_department}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">User ID:</span> <span className="font-mono text-slate-600 text-[11px]">{selectedLog.user_id}</span></div>
                    </div>
                  </div>

                  {/* Affected Target Card */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1.5">
                      <Tag className="w-4 h-4 text-indigo-600" /> Affected Target & Record
                    </h4>
                    <div className="text-xs space-y-1.5">
                      <div className="flex justify-between"><span className="text-slate-500">Module / Resource:</span> <span className="font-semibold text-slate-900">{selectedLog.resource}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Target Record:</span> <span className="font-mono text-slate-800">{selectedLog.target_name}</span></div>
                      {selectedLog.target_employee_name !== '-' && (
                        <div className="flex justify-between"><span className="text-slate-500">Affected Employee:</span> <span className="font-bold text-slate-900">{selectedLog.target_employee_name}</span></div>
                      )}
                      {selectedLog.target_employee_code !== '-' && (
                        <div className="flex justify-between"><span className="text-slate-500">Target Employee ID:</span> <span className="font-mono font-semibold text-indigo-700">{selectedLog.target_employee_code}</span></div>
                      )}
                      <div className="flex justify-between"><span className="text-slate-500">Record ID:</span> <span className="font-mono text-slate-600 text-[11px]">{selectedLog.resource_id}</span></div>
                    </div>
                  </div>
                </div>

                {/* Technical Context Card */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Cpu className="w-4 h-4 text-slate-600" /> Session & Technical Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div><span className="text-slate-500 block">Timestamp:</span> <span className="font-mono text-slate-900">{formatTimestamp(selectedLog.timestamp)}</span></div>
                    <div><span className="text-slate-500 block">Result Status:</span> <span className="font-semibold text-emerald-600">{selectedLog.result}</span></div>
                    <div><span className="text-slate-500 block">IP Address:</span> <span className="font-mono text-slate-900">{selectedLog.ip_address}</span></div>
                    <div><span className="text-slate-500 block">Request ID:</span> <span className="font-mono text-slate-700">{selectedLog.request_id}</span></div>
                  </div>
                </div>

                {/* Before vs After Data Diff Table */}
                {selectedLog.diff && selectedLog.diff.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700">Data Change Diff (Before vs After)</h4>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-100 text-slate-600 uppercase font-semibold text-[10px]">
                          <tr>
                            <th className="py-2.5 px-3 text-left">Field</th>
                            <th className="py-2.5 px-3 text-left text-rose-700 bg-rose-50/50">Previous Value</th>
                            <th className="py-2.5 px-3 text-left text-emerald-700 bg-emerald-50/50">New Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 font-mono text-[11px]">
                          {selectedLog.diff.map((d, i) => (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="py-2 px-3 font-sans font-medium text-slate-800">{d.field}</td>
                              <td className="py-2 px-3 text-rose-700 bg-rose-50/30 break-all">
                                {typeof d.old === 'object' ? JSON.stringify(d.old) : String(d.old)}
                              </td>
                              <td className="py-2 px-3 text-emerald-700 bg-emerald-50/30 break-all">
                                {typeof d.new === 'object' ? JSON.stringify(d.new) : String(d.new)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end">
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-semibold transition-colors"
                >
                  Close Inspection
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AuditLogs;
